// components/models/IdeogramControlPanel.tsx
import React, { memo, useCallback } from 'react';
import AutoGrowingTextarea from '../AutoGrowingTextarea';
import RangeControl from '../ui/RangeControl';
import Select from '../ui/Select';
import Switch from '../ui/Switch';
import {
  IdeogramEditParams,
  IdeogramRenderingSpeed,
  IdeogramV4Resolution,
} from '#types/types';
import { useKernel } from '@/kernel/Kernel';

export const getDefaultIdeogramEditParams = (): IdeogramEditParams => ({
  prompt: '',
  seed: -1,
  image_weight: 50,
  resolution: 'AUTO',
  rendering_speed: 'DEFAULT',
  enable_copyright_detection: false,
});

const RENDERING_SPEED_OPTIONS: { label: string; value: IdeogramRenderingSpeed }[] = [
  { label: 'Turbo', value: 'TURBO' },
  { label: 'Default', value: 'DEFAULT' },
  { label: 'Quality (slowest)', value: 'QUALITY' },
];
const RESOLUTION_OPTIONS: { label: string; value: IdeogramV4Resolution }[] = [
  { label: 'Auto', value: 'AUTO' },
  { label: 'Square 2048x2048', value: '2048x2048' },
  { label: 'Portrait 1440x2880', value: '1440x2880' },
  { label: 'Landscape 2880x1440', value: '2880x1440' },
  { label: 'Portrait 1664x2496', value: '1664x2496' },
  { label: 'Landscape 2496x1664', value: '2496x1664' },
  { label: 'Portrait 1792x2240', value: '1792x2240' },
  { label: 'Landscape 2240x1792', value: '2240x1792' },
  { label: 'Portrait 1440x2560', value: '1440x2560' },
  { label: 'Landscape 2560x1440', value: '2560x1440' },
  { label: 'Portrait 1600x2560', value: '1600x2560' },
  { label: 'Landscape 2560x1600', value: '2560x1600' },
  { label: 'Portrait 1728x2304', value: '1728x2304' },
  { label: 'Landscape 2304x1728', value: '2304x1728' },
  { label: 'Portrait 1296x3168', value: '1296x3168' },
  { label: 'Landscape 3168x1296', value: '3168x1296' },
  { label: 'Portrait 1152x2944', value: '1152x2944' },
  { label: 'Landscape 2944x1152', value: '2944x1152' },
  { label: 'Portrait 1248x3328', value: '1248x3328' },
  { label: 'Landscape 3328x1248', value: '3328x1248' },
  { label: 'Portrait 1024x3072', value: '1024x3072' },
  { label: 'Landscape 3072x1024', value: '3072x1024' },
  { label: 'Portrait 1280x3072', value: '1280x3072' },
  { label: 'Landscape 3072x1280', value: '3072x1280' },
];

const RENDERING_SPEED_LABELS = RENDERING_SPEED_OPTIONS.map(o => o.label);
const RESOLUTION_LABELS = RESOLUTION_OPTIONS.map(o => o.label);
const FULL_WIDTH_STYLE = { width: '100%' } as const;
const NO_TEXTAREA_STYLE_OVERRIDES = {};

interface IdeogramControlPanelProps {
  params: IdeogramEditParams;
  setModelParams: (
    updater: (prevParams: { [model: string]: IdeogramEditParams }) => { [model: string]: IdeogramEditParams }
  ) => void;
  diffusionModel: string;
}

const IdeogramControlPanel: React.FC<IdeogramControlPanelProps> = ({
  params, setModelParams, diffusionModel
}) => {
  const { kernel } = useKernel();

  const updateParam = useCallback((name: keyof IdeogramEditParams, value: unknown) => {
    setModelParams(prevModelParams => {
      // Fall back to defaults so the first edit always works even if params weren't initialized.
      const currentParams = prevModelParams[diffusionModel] ?? getDefaultIdeogramEditParams();
      return {
        ...prevModelParams,
        [diffusionModel]: { ...currentParams, [name]: value },
      };
    });
    kernel.current?.logUserAction('Setting Changed', {
      setting_name: name,
      value: typeof value === 'string' ? undefined : value,
      value_length: typeof value === 'string' ? value.length : undefined,
      model_name: diffusionModel,
      source_component: 'IdeogramControlPanel',
    });
  }, [diffusionModel, setModelParams, kernel]);

  const handleTextChange = useCallback((
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    updateParam(e.target.name as keyof IdeogramEditParams, e.target.value);
  }, [updateParam]);

  const handleRangeChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateParam(e.target.name as keyof IdeogramEditParams, parseFloat(e.target.value));
  }, [updateParam]);

  const handleResolutionChange = useCallback((label: string) => {
    updateParam('resolution', RESOLUTION_OPTIONS.find(o => o.label === label)?.value ?? 'AUTO');
  }, [updateParam]);

  const handleRenderingSpeedChange = useCallback((label: string) => {
    updateParam('rendering_speed', RENDERING_SPEED_OPTIONS.find(o => o.label === label)?.value ?? 'DEFAULT');
  }, [updateParam]);

  const handleCopyrightDetectionChange = useCallback((checked: boolean) => {
    updateParam('enable_copyright_detection', checked);
  }, [updateParam]);

  return (
    <>
      <div className="mb-4">
        <AutoGrowingTextarea
          label="Prompt"
          placeholder="Describe how to reimagine the image..."
          description="With a source image this remixes the area; without one it generates from text."
          name="prompt"
          value={params.prompt ?? ''}
          onChange={handleTextChange}
          onEnterPress={kernel.current?.triggerGenerate}
          maxHeight={600}
          labelClassName="block text-lg font-semibold text-white mb-2"
        />
      </div>

      <div className="mb-4">
        <RangeControl
          label="Image Weight"
          name="image_weight"
          min={1} max={100} step={1}
          value={params.image_weight ?? 50}
          onChange={handleRangeChange}
          description="Used only when a source image exists. Higher = closer to the source; lower = more creative."
        />
      </div>

      <div className="mb-4">
        <Select
          id="ideogramV4Resolution"
          label="Resolution"
          value={RESOLUTION_OPTIONS.find(o => o.value === params.resolution)?.label ?? 'Auto'}
          options={RESOLUTION_LABELS}
          onChange={handleResolutionChange}
          style={FULL_WIDTH_STYLE}
        />
      </div>

      <div className="mb-4">
        <Select
          id="ideogramRenderingSpeed"
          label="Rendering Speed"
          value={RENDERING_SPEED_OPTIONS.find(o => o.value === params.rendering_speed)?.label ?? 'Default'}
          options={RENDERING_SPEED_LABELS}
          onChange={handleRenderingSpeedChange}
          style={FULL_WIDTH_STYLE}
        />
        <p className="text-xs text-[#4D4D4D] mt-1">Trades generation speed against final image quality.</p>
      </div>

      <div className="mb-4 flex items-center space-x-2">
        <Switch
          checked={params.enable_copyright_detection === true}
          onCheckedChange={handleCopyrightDetectionChange}
          labelRight="Copyright detection"
        />
      </div>
    </>
  );
};

export default memo(IdeogramControlPanel);
